import type { ICustomerNotificationsSummaryResponse } from '~/types/orderNotifications';

const REFRESH_TTL_MS = 60_000;

export const useCustomerNotificationsBadge = () => {
  const { $api } = useNuxtApp();
  const unreadCount = useState<number>('customer-notifications-unread-count', () => 0);
  const unreadByOrder = useState<Record<string, number>>('customer-notifications-unread-by-order', () => ({}));
  const loading = useState<boolean>('customer-notifications-summary-loading', () => false);
  const refreshQueued = useState<boolean>('customer-notifications-summary-refresh-queued', () => false);
  const readPending = useState<Record<string, boolean>>('customer-notifications-read-pending', () => ({}));
  const lastLoadedAt = useState<number>('customer-notifications-last-loaded-at', () => 0);

  const displayUnreadCount = computed(() => unreadCount.value > 99 ? '99+' : String(unreadCount.value));

  const resetUnreadCount = () => {
    unreadCount.value = 0;
    unreadByOrder.value = {};
    readPending.value = {};
    refreshQueued.value = false;
    lastLoadedAt.value = 0;
  };

  const refreshUnreadCount = async (force = false) => {
    if (!import.meta.client) {
      return;
    }
    if (loading.value) {
      refreshQueued.value = refreshQueued.value || force;
      return;
    }
    if (!force && lastLoadedAt.value && Date.now() - lastLoadedAt.value < REFRESH_TTL_MS) {
      return;
    }

    loading.value = true;
    try {
      const response: any = await $api.agent.get('/order-notifications/summary');
      const data = response?._data as ICustomerNotificationsSummaryResponse | undefined;
      unreadCount.value = Math.max(0, Number(data?.unread_count || 0));
      unreadByOrder.value = data?.orders || {};
      lastLoadedAt.value = Date.now();
    } catch (_) {
      lastLoadedAt.value = 0;
    } finally {
      loading.value = false;
      if (refreshQueued.value) {
        refreshQueued.value = false;
        refreshUnreadCount(true);
      }
    }
  };

  const markOrderRead = async (orderId: number) => {
    const key = String(orderId);
    if (!orderId || readPending.value[key]) {
      return;
    }

    readPending.value = { ...readPending.value, [key]: true };
    try {
      await $api.agent.post(`/order-notifications/${orderId}/read`, { body: {} });
      const orderUnreadCount = Math.max(0, Number(unreadByOrder.value[key] || 0));
      unreadCount.value = Math.max(0, unreadCount.value - orderUnreadCount);
      unreadByOrder.value = { ...unreadByOrder.value, [key]: 0 };
      lastLoadedAt.value = Date.now();
      refreshUnreadCount(true);
    } catch (_) {
      lastLoadedAt.value = 0;
    } finally {
      const pending = { ...readPending.value };
      delete pending[key];
      readPending.value = pending;
    }
  };

  return {
    unreadCount,
    unreadByOrder,
    displayUnreadCount,
    refreshUnreadCount,
    markOrderRead,
    resetUnreadCount
  };
};
